import { VStack, Text, Circle, HStack, Icon } from '@chakra-ui/react';

import { format } from 'date-fns';
import * as fp from 'lodash/fp';
import formatPriceNumber from '~/utils/formatPriceNumber';

import { AiOutlineCheckCircle, AiOutlineExclamationCircle } from 'react-icons/ai';
import { OrderStatus, PaymentInfo } from '~/types/order';
import { useParams } from 'react-router-dom';

type StatusNoticeProps = {
  title: string;
  message: string;
};

function StatusNotice({ title, message }: StatusNoticeProps) {
  return (
    <VStack
      align="stretch"
      px={2}
      py={4}
      borderRadius="5px"
      borderWidth="1px"
      borderColor="gray.400"
    >
      <HStack align="stretch">
        <Circle my={1.5} w="12px" h="12px" bgColor="danger.600" />
        <VStack align="flex-start">
          <Text fontSize="sm" fontWeight="bold">
            {title}
          </Text>
          <Text fontSize="xs" wordBreak="break-word">
            {message}
          </Text>
        </VStack>
      </HStack>
    </VStack>
  );
}

type StatusDescriptionProps = {
  type: 'success' | 'error';
  title: string;
  message: string;
  children?: React.ReactNode;
};

function StatusDescription({ type, title, message, children }: StatusDescriptionProps) {
  return (
    <VStack px={2} py={4} align="stretch" spacing={2} borderRadius="5px" bgColor="gray.200">
      <HStack>
        {fp.cond<'success' | 'error', JSX.Element | null>([
          [
            fp.eq('success'),
            () => <Icon as={AiOutlineCheckCircle} boxSize="1.5rem" color="#43CE62" />,
          ],
          [
            fp.eq('error'),
            () => <Icon as={AiOutlineExclamationCircle} boxSize="1.5rem" color="danger.600" />,
          ],
          [fp.T, () => null],
        ])(type)}
        <Text fontSize="sm" fontWeight="bold">
          {title}
        </Text>
      </HStack>

      <Text fontSize="xs">{message}</Text>

      {children}
    </VStack>
  );
}

// ** five different Status Notice **

function PayingNotice() {
  return <StatusNotice title="等待付款中" message="等待付款流程認證成功，訂單才正式成立" />;
}

type StoreCancelNoticeProps = {
  reasonsForCancellation?: Array<string>;
  paymentInfo?: PaymentInfo;
  total: number;
};

function StoreCancelNotice({ reasonsForCancellation, paymentInfo, total }: StoreCancelNoticeProps) {
  const cancelReasonsString = reasonsForCancellation?.join('、') || '';

  return (
    <VStack align="stretch" spacing={3}>
      <StatusNotice title="訂單取消" message={`取消原因：${cancelReasonsString}`} />
      <StatusDescription
        type="success"
        title="店家已退款"
        message="店家已從後台操作退款，因綁定的信用卡不同，線上操作退款後，實際款項入帳日期會因各家銀行退款流程不同。"
      >
        <VStack p={2} align="flex-start" borderRadius="5px" bgColor="white">
          <Text fontSize="xs">
            訂單交易日期：
            {paymentInfo?.paidAt ? format(paymentInfo.paidAt.toDate(), 'yyyy/MM/dd HH:mm') : '無'}
          </Text>
          <Text fontSize="xs">交易金額：{formatPriceNumber(total)}</Text>
        </VStack>
      </StatusDescription>
    </VStack>
  );
}

type CustomerCancelNoticeProps = {
  paymentInfo?: PaymentInfo;
  total: number;
};

function CustomerCancelNotice({ paymentInfo, total }: CustomerCancelNoticeProps) {
  return (
    <VStack align="stretch" spacing={3}>
      <StatusNotice title="訂單取消" message="取消原因：消費者自行取消" />
      <StatusDescription
        type="success"
        title="店家已退款"
        message="店家已從後台操作退款，因綁定的信用卡不同，線上操作退款後，實際款項入帳日期會因各家銀行退款流程不同。"
      >
        <VStack p={2} align="flex-start" borderRadius="5px" bgColor="white">
          <Text fontSize="xs">
            訂單交易日期：
            {paymentInfo?.paidAt ? format(paymentInfo.paidAt.toDate(), 'yyyy/MM/dd HH:mm') : '無'}
          </Text>
          <Text fontSize="xs">交易金額：{formatPriceNumber(total)}</Text>
        </VStack>
      </StatusDescription>
    </VStack>
  );
}

function AbandonmentNotice({ reasonsForCancellation = [] }: { reasonsForCancellation?: string[] }) {
  const { enterpriseGroupBuyId = '' } = useParams();

  return (
    <VStack align="stretch" spacing={3}>
      <StatusNotice
        title="客戶棄單"
        message={
          !!enterpriseGroupBuyId
            ? `取消原因：團購取消-${reasonsForCancellation.join('、')}`
            : '取消原因：客戶未取餐'
        }
      />
      <StatusDescription
        type="error"
        title={!!enterpriseGroupBuyId ? '店家不退款' : '客戶棄單將不退款'}
        message={
          !!enterpriseGroupBuyId
            ? '此筆訂單無法退費，如果疑問請洽團購主辦人。'
            : '此筆訂單已出餐，視同消費者放棄該餐點，故該單無法退費。'
        }
      />
    </VStack>
  );
}

function PaymentFailureNotice() {
  return (
    <VStack align="stretch" spacing={3}>
      <StatusNotice title="付款失敗" message="取消原因：線上刷卡程序驗證失敗/信用卡失效" />
      <StatusDescription
        type="error"
        title="付款程序未完成"
        message="此筆訂單因付款失敗，並沒有送出到店家端，建議您換其他信用卡進行付費。"
      />
    </VStack>
  );
}

type NotCreatedOrFailOrderNoticeProps = {
  status: OrderStatus;
  reasonsForCancellation?: Array<string>;
  paymentInfo?: PaymentInfo;
  total: number;
};

export default function NotCreatedOrFailOrderNotice({
  status,
  reasonsForCancellation,
  paymentInfo,
  total,
}: NotCreatedOrFailOrderNoticeProps) {
  return fp.cond<OrderStatus, JSX.Element | null>([
    [fp.eq(OrderStatus.UNPAID), () => <PayingNotice />],
    [
      fp.eq(OrderStatus.ABANDONED),
      () => <AbandonmentNotice reasonsForCancellation={reasonsForCancellation} />,
    ],
    [fp.eq(OrderStatus.PAYMENT_FAILED), () => <PaymentFailureNotice />],
    [
      fp.eq(OrderStatus.FAILED),
      () => (
        <StoreCancelNotice
          reasonsForCancellation={reasonsForCancellation}
          paymentInfo={paymentInfo}
          total={total}
        />
      ),
    ],
    [
      fp.eq(OrderStatus.CANCELED),
      () => <CustomerCancelNotice paymentInfo={paymentInfo} total={total} />,
    ],
    [fp.T, fp.always(null)],
  ])(status);
}
